import * as THREE from 'three'
import { Text } from '@react-three/drei'
import React, { createRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { RulerInformation } from './rulers'
import { useMeasures } from '../../../../hooks/state/useMeasures'

import {
  useSelectedNumberByVariableKey,
  useSelectedValueAttributeByKey,
  useSelectedValueByVariableKey,
  useSelectionByVariableId,
  useSelectionByVariableKey,
} from '@k3/core'

export const Ruler = (props: { rule: RulerInformation }): JSX.Element => {
  const { isQuer, depth, userWidth } = useMeasures()
  return (
    <mesh
      rotation={isQuer ? [0, 0, 0] : [0, ((90 % 360) * Math.PI) / 180, 0]}
      position={[isQuer ? 0 : -depth / 2, 0, isQuer ? 0 : userWidth / 2]}
    >
      <Line {...props.rule} />
    </mesh>
  )
}

const StartEndLine = (props: {
  start: any
  direction: string
  pos: string
}) => {
  let ref: any = createRef<THREE.Line | null>()
  const startEndPos = props.pos == 'links' ? 0.08 : -0.08
  useFrame(() => {
    const start = props.start.map((s: number, i: number) => {
      if (props.direction == 'x') {
        return i == 2 ? props.start[2] - 0.08 : s
      } else if (props.direction == 'y') {
        return i == 0 ? props.start[0] + startEndPos : s
      } else if (props.direction == 'z') {
        return i == 0 ? props.start[0] - 0.08 : s
      }
    })
    if (ref.current) {
      ref.current.geometry.setFromPoints(
        [start, props.start].map((point) => new THREE.Vector3(...point))
      )
    }
  })

  return (
    <line ref={ref}>
      <bufferGeometry />
      <lineBasicMaterial color="black" linewidth={0.1} />
    </line>
  )
}

const Line = (props: RulerInformation) => {
  const multiplier = useSelectedValueByVariableKey('multiplier') || null
  const multiplierAttr =
    useSelectedValueAttributeByKey('multiplier', 'value') || null
  const multiplierAttrDecimal =
    useSelectedValueAttributeByKey('multiplier', 'decimal') || null
  const oldModifier = useSelectedNumberByVariableKey('rulerMultiplier') ?? 1

  const rulerMultiplier = (
    multiplierAttr ? multiplierAttr.value : oldModifier
  ) as number
  const multiplierLabel = (
    multiplier ? multiplier?.label : oldModifier > 1 ? 'mm' : 'cm'
  ) as string

  const decimalPlace = (
    multiplierAttrDecimal !== null
      ? multiplierAttrDecimal.value
      : oldModifier > 1
      ? 0
      : 2
  ) as number

  let ref: any = createRef<THREE.Line | null>()
  let ref1: any = createRef<THREE.Line | null>()
  const offsetLine =
    props.direction === 'y'
      ? 0.15
      : 0.028 *
        (multiplierLabel.length +
          props.length?.toFixed(decimalPlace).toString().length +
          1)

  const end = props.startPoint.map((s: number, i: number) => {
    if (props.direction == 'x') {
      return i == 0 ? props.startPoint[0] + props.endPoint[0] - offsetLine : s
    } else if (props.direction == 'y') {
      return i == 1
        ? (props.startPoint[1] + props.endPoint[1]) / 2 - offsetLine / 2
        : s
    } else if (props.direction == 'z') {
      return i == 2
        ? (props.startPoint[2] + props.endPoint[2]) / 2 - offsetLine
        : s
    }
  })

  const start = props.endPoint.map((s: number, i: number) => {
    if (props.direction == 'x') {
      return i == 0 ? props.endPoint[0] + props.startPoint[0] + offsetLine : s
    } else if (props.direction == 'y') {
      return i == 1
        ? (props.startPoint[1] + props.endPoint[1]) / 2 + offsetLine / 2
        : s
    } else if (props.direction == 'z') {
      return i == 2
        ? (props.startPoint[2] + props.endPoint[2]) / 2 + offsetLine
        : s
    }
  })

  useFrame(() => {
    if (ref.current) {
      ref.current.geometry.setFromPoints(
        [start, props.endPoint].map((point) => new THREE.Vector3(...point))
      )
    }
    if (ref1.current) {
      ref1.current.geometry.setFromPoints(
        [props.startPoint, end].map((point) => new THREE.Vector3(...point))
      )
    }
  })

  const formattedNumber = (rulerMultiplier * props.length)
    .toFixed(decimalPlace)
    // @ts-ignore
    .toLocaleString('de-DE', {
      minimumFractionDigits: decimalPlace,
      maximumFractionDigits: decimalPlace,
    })

  return (
    <group>
      <StartEndLine
        start={props.startPoint}
        direction={props.direction}
        pos={props.pos}
      />
      <line ref={ref}>
        <bufferGeometry />
        <lineBasicMaterial color="black" linewidth={1.1} />
      </line>

      <Text
        position={props.textPosition as any}
        color="black"
        rotation={props.textRotation as any}
        fontSize={0.08}
      >
        {formattedNumber} {multiplierLabel}
      </Text>

      <line ref={ref1}>
        <bufferGeometry />
        <lineBasicMaterial color="black" linewidth={1.1} />
      </line>
      <StartEndLine
        start={props.endPoint}
        direction={props.direction}
        pos={props.pos}
      />
    </group>
  )
}
